/**
 * AccountPage.jsx
 * Player account settings — profile pic, username, personal details,
 * password, PIN, and account management.
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  doc, getDoc, setDoc, runTransaction, serverTimestamp,
} from 'firebase/firestore';
import {
  updatePassword, reauthenticateWithCredential,
  EmailAuthProvider, onAuthStateChanged, updateProfile,
} from 'firebase/auth';
import {
  ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject,
} from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, auth, storage, functions } from '../services/firebase';
import {
  getProfileImageUrl, validateAvatarFile, extensionForFile, DEFAULT_PROFILE_IMAGE,
} from '../utils/profileImage';
import { DEFAULT_NOTIFICATION_PREFERENCES, getNotificationPreferences } from '../utils/notificationPreferences';
import './AccountPage.css';

function AccountPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [user, setUser]       = useState(null);
  const [userDoc, setUserDoc] = useState(null);
  const [loading, setLoading] = useState(true);

  // Editable fields
  const [username, setUsername]       = useState('');
  const [displayName, setDisplayName] = useState('');
  const [phone, setPhone]             = useState('');
  const [address, setAddress]         = useState('');
  const [photoURL, setPhotoURL]       = useState(null); // resolved display image (saved URL, local preview, or default)

  // Pending avatar upload — selected but not yet saved
  const [pendingAvatarFile, setPendingAvatarFile] = useState(null);
  const [avatarUploadPct,   setAvatarUploadPct]   = useState(null);
  const [savingProfile,     setSavingProfile]     = useState(false);

  // Status messages
  const [usernameStatus,   setUsernameStatus]   = useState(null);
  const [usernameAvailable, setUsernameAvailable] = useState(null); // null=unchecked true=available false=taken
  const [profileStatus,    setProfileStatus]    = useState(null);
  const [passwordStatus,   setPasswordStatus]   = useState(null);

  // Phase 5 — notification preferences
  const [notifPrefs, setNotifPrefs]             = useState(DEFAULT_NOTIFICATION_PREFERENCES);
  const [notifStatus, setNotifStatus]           = useState(null);
  const [savingNotifPrefs, setSavingNotifPrefs] = useState(false);

  // Phase 7 — public stable profile (bio + slug)
  const [stableSlug, setStableSlug] = useState(null);
  const [bio, setBio]               = useState('');
  const [bioStatus, setBioStatus]   = useState(null);
  const [savingBio, setSavingBio]   = useState(false);

  // Profile Visibility toggle — defaults true (public) to match existing
  // behaviour for every profile created before this field existed.
  const [profilePublic, setProfilePublic] = useState(true);
  const [savingVisibility, setSavingVisibility] = useState(false);

  // Password fields
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword]         = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => onAuthStateChanged(auth, async u => {
    if (!u) { navigate('/login'); return; }
    setUser(u);
    const snap = await getDoc(doc(db, 'users', u.uid));
    const data = snap.exists() ? snap.data() : null;
    if (data) {
      setUserDoc(data);
      setUsername(data.username || '');
      setDisplayName(data.displayName || '');
      setPhone(data.phone || '');
      setAddress(data.address || '');
      setStableSlug(data.stableSlug || null);
    }
    setNotifPrefs(getNotificationPreferences(data));
    setPhotoURL(getProfileImageUrl(data, u));

    // Phase 7 backfill — existing players who already claimed a Stable Name
    // before this phase shipped won't have a stableSlug yet. Claim one
    // opportunistically on mount so their public profile becomes reachable
    // without requiring them to re-save their Stable Name.
    if (data?.username && !data?.stableSlug) {
      try {
        const claim = httpsCallable(functions, 'claimStableSlug');
        const res = await claim({ stableName: data.username });
        setStableSlug(res.data?.stableSlug || null);
      } catch (e) {
        console.error('[AccountPage] Backfill claimStableSlug error:', e.code, e.message);
      }
    }

    // Phase 7 — bio comes from the public player_profiles mirror, not users/{uid}
    try {
      const profSnap = await getDoc(doc(db, 'player_profiles', u.uid));
      if (profSnap.exists()) {
        setBio(profSnap.data().bio || '');
        // profilePublic is undefined for every profile created before this
        // field existed — treat that as public (existing default behaviour).
        setProfilePublic(profSnap.data().profilePublic !== false);
      }
    } catch (e) {
      console.error('[AccountPage] player_profiles bio load error:', e.code, e.message);
    }

    setLoading(false);
  }), []);

  // ── Live username availability check ────────────────────────────
  useEffect(() => {
    const clean = username.toLowerCase().trim();
    if (clean.length < 3 || !/^[a-z0-9_]+$/.test(clean)) {
      setUsernameAvailable(null);
      return;
    }
    // Already the user's current username — no need to check
    if (clean === userDoc?.username) {
      setUsernameAvailable(true);
      return;
    }
    const timeout = setTimeout(async () => {
      try {
        const snap = await getDoc(doc(db, 'usernames', clean));
        setUsernameAvailable(!snap.exists());
      } catch {
        setUsernameAvailable(null);
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [username, userDoc?.username]);

  // ── Profile photo ───────────────────────────────────────────────
  function handlePhotoClick() {
    fileInputRef.current?.click();
  }

  function handlePhotoChange(e) {
    const file = e.target.files?.[0];
    // Reset the input so selecting the same file again still fires onChange
    e.target.value = '';
    if (!file) return;

    const err = validateAvatarFile(file);
    if (err) {
      setProfileStatus({ type: 'error', msg: err });
      return;
    }

    setPendingAvatarFile(file);
    setProfileStatus(null);

    // Local preview only — the actual Storage upload happens on Save Profile.
    const reader = new FileReader();
    reader.onload = ev => setPhotoURL(ev.target.result);
    reader.readAsDataURL(file);
  }

  /**
   * Uploads pendingAvatarFile to users/{uid}/profile/avatar.{ext}, deleting
   * the previous object first if its extension differs, and returns the
   * fields to merge into the users/{uid} doc.
   */
  async function uploadPendingAvatar(currentUser) {
    const file = pendingAvatarFile;
    const ext = extensionForFile(file);
    const path = `users/${currentUser.uid}/profile/avatar.${ext}`;

    // Best-effort cleanup of a previous avatar with a different extension —
    // never block the new upload if this fails.
    const oldPath = userDoc?.avatarStoragePath;
    if (oldPath && oldPath !== path) {
      try { await deleteObject(storageRef(storage, oldPath)); } catch { /* ignore */ }
    }

    const fileRef = storageRef(storage, path);
    const task = uploadBytesResumable(fileRef, file, { contentType: file.type });

    await new Promise((resolve, reject) => {
      task.on('state_changed',
        snap => setAvatarUploadPct(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
        reject,
        resolve);
    });

    const downloadURL = await getDownloadURL(fileRef);
    return { photoURL: downloadURL, avatarUrl: downloadURL, avatarStoragePath: path };
  }

  // ── Save profile ────────────────────────────────────────────────
  async function saveProfile() {
    const currentUser = auth.currentUser;
    if (!currentUser) return;
    setSavingProfile(true);
    const uploadingPhoto = Boolean(pendingAvatarFile);
    if (uploadingPhoto) {
      setAvatarUploadPct(0);
      setProfileStatus({ type: 'info', msg: 'Uploading photo…' });
    }
    try {
      let avatarFields = {};
      if (uploadingPhoto) {
        avatarFields = await uploadPendingAvatar(currentUser);
      }

      // Private user doc
      await setDoc(doc(db, 'users', currentUser.uid), {
        displayName,
        phone,
        address,
        ...avatarFields,
        updatedAt: serverTimestamp(),
      }, { merge: true });

      // Keep public profile in sync (display name + avatar — username set separately)
      await setDoc(doc(db, 'player_profiles', currentUser.uid), {
        displayName,
        ...(avatarFields.photoURL ? { photoURL: avatarFields.photoURL } : {}),
        updatedAt: serverTimestamp(),
      }, { merge: true });

      // Keep Firebase Auth's own profile in sync too, where supported.
      if (avatarFields.photoURL) {
        try {
          await updateProfile(currentUser, { photoURL: avatarFields.photoURL });
        } catch (e) {
          console.warn('[AccountPage] auth updateProfile(photoURL) failed:', e.code, e.message);
        }
      }

      if (avatarFields.photoURL) {
        setPhotoURL(avatarFields.photoURL);
        setUserDoc(prev => ({ ...(prev || {}), ...avatarFields }));
        setPendingAvatarFile(null);
      }

      setProfileStatus({
        type: 'success',
        msg: uploadingPhoto ? 'Profile photo updated' : 'Profile updated successfully',
      });
    } catch (e) {
      console.error('Profile save error:', e.code, e.message);
      setProfileStatus({ type: 'error', msg: `Failed to save profile: ${e.code || e.message}` });
    } finally {
      setAvatarUploadPct(null);
      setSavingProfile(false);
    }
  }

  // ── Remove profile photo ────────────────────────────────────────
  async function removePhoto() {
    const currentUser = auth.currentUser;
    if (!currentUser) return;
    setSavingProfile(true);
    try {
      const path = userDoc?.avatarStoragePath;
      if (path) {
        try { await deleteObject(storageRef(storage, path)); } catch { /* best effort */ }
      }
      await setDoc(doc(db, 'users', currentUser.uid), {
        photoURL: null,
        avatarUrl: null,
        avatarStoragePath: null,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      await setDoc(doc(db, 'player_profiles', currentUser.uid), {
        photoURL: null,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      try { await updateProfile(currentUser, { photoURL: null }); } catch { /* best effort */ }

      setPendingAvatarFile(null);
      setUserDoc(prev => ({ ...(prev || {}), photoURL: null, avatarUrl: null, avatarStoragePath: null }));
      setPhotoURL(DEFAULT_PROFILE_IMAGE);
      setProfileStatus({ type: 'success', msg: 'Profile photo removed' });
    } catch (e) {
      console.error('Remove photo error:', e.code, e.message);
      setProfileStatus({ type: 'error', msg: `Failed to remove photo: ${e.code || e.message}` });
    } finally {
      setSavingProfile(false);
    }
  }

  // ── Save username (atomic transaction) ──────────────────────────
  async function saveUsername() {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setUsernameStatus({ type: 'error', msg: 'Not logged in — please refresh' });
      return;
    }
    const clean = username.toLowerCase().trim();
    if (!/^[a-z0-9_]{3,20}$/.test(clean)) {
      setUsernameStatus({ type: 'error', msg: 'Must be 3–20 characters — letters, numbers and underscores only' });
      return;
    }
    setUsernameStatus({ type: 'info', msg: 'Checking availability…' });
    try {
      const usernameRef = doc(db, 'usernames', clean);
      const userRef     = doc(db, 'users', currentUser.uid);
      const profileRef  = doc(db, 'player_profiles', currentUser.uid);

      await runTransaction(db, async (tx) => {
        const usernameSnap = await tx.get(usernameRef);
        // Already claimed by someone else
        if (usernameSnap.exists() && usernameSnap.data().uid !== currentUser.uid) {
          throw new Error('TAKEN');
        }
        // Release old username if different
        const userSnap = await tx.get(userRef);
        const oldUsername = userSnap.exists() ? userSnap.data().username : null;
        if (oldUsername && oldUsername !== clean) {
          tx.delete(doc(db, 'usernames', oldUsername));
        }
        const profileSnap = await tx.get(profileRef);
        // Claim new username
        tx.set(usernameRef, { uid: currentUser.uid, claimedAt: serverTimestamp() });
        // Update user document
        tx.set(userRef, { username: clean, usernameSetAt: serverTimestamp() }, { merge: true });
        // Write public profile — readable by anyone for search results.
        // profilePublic is only ever set here on first creation — merge:true
        // on every later Stable Name change must never stomp back over an
        // existing Profile Visibility toggle value. Public by default,
        // matching every profile created before this field existed.
        tx.set(profileRef, {
          username:       clean,
          displayName:    userSnap.exists() ? (userSnap.data().displayName || '') : '',
          foundingMember: userSnap.exists() ? (userSnap.data().foundingMember || false) : false,
          ...(profileSnap.exists() ? {} : { profilePublic: true }),
          updatedAt:      serverTimestamp(),
        }, { merge: true });
      });

      setUsernameAvailable(true);
      setUsernameStatus({ type: 'success', msg: `✓ Stable name set to @${clean}` });

      // Phase 7 — claim/refresh the public profile slug (/stable/:slug) for
      // this new Stable Name. Best-effort — a failure here must not undo
      // the username save that already succeeded above.
      try {
        const claim = httpsCallable(functions, 'claimStableSlug');
        const res = await claim({ stableName: clean });
        setStableSlug(res.data?.stableSlug || null);
      } catch (slugErr) {
        console.error('[AccountPage] claimStableSlug error:', slugErr.code, slugErr.message);
      }
    } catch (e) {
      if (e.message === 'TAKEN') {
        setUsernameAvailable(false);
        setUsernameStatus({ type: 'error', msg: `@${clean} is already taken — choose another` });
      } else if (e.code === 'permission-denied') {
        setUsernameStatus({ type: 'error', msg: 'Permission denied — please log out and back in' });
        console.error('saveUsername permission-denied:', e);
      } else {
        setUsernameStatus({ type: 'error', msg: `Something went wrong: ${e.message}` });
        console.error('saveUsername error:', e.code, e.message);
      }
    }
  }

  // ── Change password ─────────────────────────────────────────────
  async function changePassword() {
    if (newPassword !== confirmPassword) {
      setPasswordStatus({ type: 'error', msg: 'Passwords do not match' });
      return;
    }
    if (newPassword.length < 8) {
      setPasswordStatus({ type: 'error', msg: 'Password must be at least 8 characters' });
      return;
    }
    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      setPasswordStatus({ type: 'success', msg: 'Password updated successfully' });
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (e) {
      setPasswordStatus({ type: 'error', msg: 'Current password incorrect or session expired' });
    }
  }

  // ── Notification preferences ────────────────────────────────────
  function toggleNotifPref(key) {
    setNotifPrefs(prev => ({ ...prev, [key]: !prev[key] }));
    setNotifStatus(null);
  }

  async function saveNotifPrefs() {
    const currentUser = auth.currentUser;
    if (!currentUser) return;
    setSavingNotifPrefs(true);
    try {
      await setDoc(doc(db, 'users', currentUser.uid), {
        notificationPreferences: notifPrefs,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setUserDoc(prev => ({ ...(prev || {}), notificationPreferences: notifPrefs }));
      setNotifStatus({ type: 'success', msg: 'Notification preferences saved' });
    } catch (e) {
      console.error('Notification preferences save error:', e.code, e.message);
      setNotifStatus({ type: 'error', msg: `Failed to save preferences: ${e.code || e.message}` });
    } finally {
      setSavingNotifPrefs(false);
    }
  }

  // ── Profile visibility toggle ────────────────────────────────────
  async function toggleProfileVisibility(nextPublic) {
    const currentUser = auth.currentUser;
    if (!currentUser) return;
    setProfilePublic(nextPublic); // optimistic — toggle should feel instant
    setSavingVisibility(true);
    try {
      await Promise.all([
        setDoc(doc(db, 'player_profiles', currentUser.uid), {
          profilePublic: nextPublic,
          updatedAt: serverTimestamp(),
        }, { merge: true }),
        setDoc(doc(db, 'users', currentUser.uid), {
          profilePublic: nextPublic,
          updatedAt: serverTimestamp(),
        }, { merge: true }),
      ]);
    } catch (e) {
      console.error('[AccountPage] Profile visibility save error:', e.code, e.message);
      setProfilePublic(!nextPublic); // roll back on failure
    } finally {
      setSavingVisibility(false);
    }
  }

  // ── Public Stable Profile bio ───────────────────────────────────
  async function saveBio() {
    const currentUser = auth.currentUser;
    if (!currentUser) return;
    setSavingBio(true);
    try {
      await setDoc(doc(db, 'player_profiles', currentUser.uid), {
        bio: bio.trim().slice(0, 160),
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setBioStatus({ type: 'success', msg: 'Bio saved' });
    } catch (e) {
      console.error('Bio save error:', e.code, e.message);
      setBioStatus({ type: 'error', msg: `Failed to save bio: ${e.code || e.message}` });
    } finally {
      setSavingBio(false);
    }
  }

  if (loading) return <div className="account-page__loading">Loading account…</div>;

  const hasCustomPhoto = Boolean(userDoc?.photoURL || userDoc?.avatarUrl);

  return (
    <div className="account-page">
      <h1 className="account-page__title">My Account</h1>

      {/* ── Profile card ─────────────────────────────────────── */}
      <div className="account-section">
        <h2 className="account-section__heading">Profile</h2>

        {/* Avatar */}
        <div className="account-avatar-row">
          <div className="account-avatar" onClick={handlePhotoClick}>
            <img
              src={photoURL || DEFAULT_PROFILE_IMAGE}
              alt="Profile"
              className="account-avatar__img"
              onError={e => { e.currentTarget.src = DEFAULT_PROFILE_IMAGE; }}
            />
            <div className="account-avatar__overlay">
              <span>{avatarUploadPct != null ? `Uploading… ${avatarUploadPct}%` : 'Change Photo'}</span>
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp"
            style={{ display: 'none' }}
            onChange={handlePhotoChange}
          />
          <div className="account-avatar-info">
            <p className="account-avatar-info__name">{displayName || 'Set your name'}</p>
            <p className="account-avatar-info__username">
              @{username || 'set a username below'}
            </p>
            {userDoc?.foundingMember && (
              <span className="account-avatar-info__badge">⭐ Founding Member</span>
            )}
            <div className="account-avatar-actions">
              <button type="button" className="account-avatar-actions__btn" onClick={handlePhotoClick}>
                Change Photo
              </button>
              {(hasCustomPhoto || pendingAvatarFile) && (
                <button
                  type="button"
                  className="account-avatar-actions__btn account-avatar-actions__btn--remove"
                  onClick={removePhoto}
                  disabled={savingProfile}
                >
                  Remove Photo
                </button>
              )}
            </div>
            {pendingAvatarFile && (
              <p className="account-avatar-info__pending">
                New photo selected — click Save Profile to upload it.
              </p>
            )}
          </div>
        </div>

        {/* Profile fields */}
        <div className="account-form">
          <div className="account-form__field">
            <label className="account-form__label">Display Name</label>
            <input
              className="account-form__input"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              placeholder="Your full name"
            />
          </div>
          <div className="account-form__field">
            <label className="account-form__label">Phone (optional)</label>
            <input
              className="account-form__input"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="+61 4XX XXX XXX"
              type="tel"
            />
          </div>
          <div className="account-form__field">
            <label className="account-form__label">Address (optional)</label>
            <input
              className="account-form__input"
              value={address}
              onChange={e => setAddress(e.target.value)}
              placeholder="Street, City, State"
            />
          </div>
        </div>

        {profileStatus && (
          <p className={`account-status account-status--${profileStatus.type}`}>
            {profileStatus.msg}
          </p>
        )}
        <button
          className="account-btn account-btn--primary"
          onClick={saveProfile}
          disabled={savingProfile}
          style={{ opacity: savingProfile ? 0.6 : 1 }}
        >
          {savingProfile ? 'Saving…' : 'Save Profile'}
        </button>
      </div>

      {/* ── Username / Stable Name ────────────────────────────── */}
      <div className="account-section">
        <h2 className="account-section__heading">Stable Name</h2>
        <p className="account-section__desc">
          Your public identity on Syndicator Showdown. Shown in chat, leaderboards, and player search.
          Choose something memorable — like your favourite racehorse or stable.
        </p>
        <div className="account-form">
          <div className="account-form__field">
            <label className="account-form__label">Stable Name</label>
            <div className="account-form__input-prefix-wrap">
              <span className="account-form__prefix">@</span>
              <input
                className="account-form__input account-form__input--prefixed"
                value={username}
                onChange={e => {
                  setUsername(e.target.value.replace(/[^a-z0-9_]/gi, '').toLowerCase());
                  setUsernameStatus(null);
                }}
                placeholder="mightandpower"
                maxLength={20}
              />
              {username.length >= 3 && (
                <span
                  className="account-form__availability"
                  style={{
                    color: usernameAvailable === true  ? '#22c55e'
                         : usernameAvailable === false ? '#ef4444'
                         : '#73859A'
                  }}
                >
                  {usernameAvailable === null  && '…'}
                  {usernameAvailable === true  && '✓'}
                  {usernameAvailable === false && '✗'}
                </span>
              )}
            </div>
            <p className="account-form__hint">
              {usernameAvailable === false
                ? `@${username} is already taken`
                : usernameAvailable === true
                ? `@${username} is available`
                : '3–20 characters. Letters, numbers, underscores. Your public identity on leaderboards and chat.'}
            </p>
          </div>
        </div>
        {usernameStatus && (
          <p className={`account-status account-status--${usernameStatus.type}`}>
            {usernameStatus.msg}
          </p>
        )}
        <button
          className="account-btn account-btn--primary"
          onClick={saveUsername}
          disabled={usernameAvailable === false || username.length < 3}
          style={{ opacity: (usernameAvailable === false || username.length < 3) ? 0.4 : 1 }}
        >
          Save Stable Name
        </button>
      </div>

      {/* ── Public Stable Profile (Phase 7) ─────────────────────── */}
      <div className="account-section">
        <h2 className="account-section__heading">Public Stable Profile</h2>
        <p className="account-section__desc">
          Your public profile shows your stable name, avatar, virtual results and
          trophy cabinet. Private details like your email, phone and address are
          never shown.
        </p>

        <label className="account-toggle-row">
          <div>
            <p className="account-toggle-row__label">Profile Visibility</p>
            <p className="account-toggle-row__hint">
              {profilePublic
                ? 'Your profile is visible to all players'
                : 'Your profile is hidden from other players'}
            </p>
          </div>
          <span className={`account-toggle ${profilePublic ? 'account-toggle--on' : ''}`}>
            <input
              type="checkbox"
              checked={profilePublic}
              disabled={savingVisibility}
              onChange={e => toggleProfileVisibility(e.target.checked)}
            />
            <span className="account-toggle__track"><span className="account-toggle__thumb" /></span>
          </span>
        </label>

        {stableSlug ? (
          <Link to={`/stable/${stableSlug}`} className="account-btn account-btn--secondary" target="_blank" rel="noreferrer">
            View Public Profile →
          </Link>
        ) : (
          <p className="account-section__desc account-section__desc--footnote">
            Set a Stable Name above to create your public profile.
          </p>
        )}

        <div className="account-form" style={{ marginTop: 16 }}>
          <div className="account-form__field">
            <label className="account-form__label">Bio / Tagline (optional)</label>
            <input
              className="account-form__input"
              value={bio}
              onChange={e => { setBio(e.target.value); setBioStatus(null); }}
              placeholder="e.g. Born to race, bred to win"
              maxLength={160}
            />
            <p className="account-form__hint">{bio.length}/160 characters — shown on your public profile.</p>
          </div>
        </div>
        {bioStatus && (
          <p className={`account-status account-status--${bioStatus.type}`}>{bioStatus.msg}</p>
        )}
        <button
          className="account-btn account-btn--primary"
          onClick={saveBio}
          disabled={savingBio}
          style={{ opacity: savingBio ? 0.6 : 1 }}
        >
          {savingBio ? 'Saving…' : 'Save Bio'}
        </button>
      </div>

      {/* ── Notification Preferences ────────────────────────────── */}
      <div className="account-section" id="notification-preferences">
        <h2 className="account-section__heading">Notification Preferences</h2>
        <p className="account-section__desc">
          Choose how Syndicator Showdown keeps you updated about your stable.
        </p>

        <div className="account-toggle-list">
          <label className="account-toggle-row">
            <div>
              <p className="account-toggle-row__label">In-app notifications</p>
              <p className="account-toggle-row__hint">Show alerts and cards inside the app.</p>
            </div>
            <span className={`account-toggle ${notifPrefs.inApp ? 'account-toggle--on' : ''}`}>
              <input
                type="checkbox"
                checked={notifPrefs.inApp}
                onChange={() => toggleNotifPref('inApp')}
              />
              <span className="account-toggle__track"><span className="account-toggle__thumb" /></span>
            </span>
          </label>

          <label className="account-toggle-row">
            <div>
              <p className="account-toggle-row__label">Email me when my runners are coming up</p>
              <p className="account-toggle-row__hint">A heads-up before your runners jump, if you're away from the app.</p>
            </div>
            <span className={`account-toggle ${notifPrefs.emailUpcomingRunner ? 'account-toggle--on' : ''}`}>
              <input
                type="checkbox"
                checked={notifPrefs.emailUpcomingRunner}
                onChange={() => toggleNotifPref('emailUpcomingRunner')}
              />
              <span className="account-toggle__track"><span className="account-toggle__thumb" /></span>
            </span>
          </label>

          <label className="account-toggle-row">
            <div>
              <p className="account-toggle-row__label">Email me race results and virtual prize updates</p>
              <p className="account-toggle-row__hint">Get notified about how your stable performed, even if you missed it live.</p>
            </div>
            <span className={`account-toggle ${notifPrefs.emailRaceResult ? 'account-toggle--on' : ''}`}>
              <input
                type="checkbox"
                checked={notifPrefs.emailRaceResult}
                onChange={() => toggleNotifPref('emailRaceResult')}
              />
              <span className="account-toggle__track"><span className="account-toggle__thumb" /></span>
            </span>
          </label>

          <label className="account-toggle-row">
            <div>
              <p className="account-toggle-row__label">Email me when a new Showdown is about to open</p>
              <p className="account-toggle-row__hint">A heads-up ~30 minutes before entries open for a new game.</p>
            </div>
            <span className={`account-toggle ${notifPrefs.emailNewShowdownOpening ? 'account-toggle--on' : ''}`}>
              <input
                type="checkbox"
                checked={notifPrefs.emailNewShowdownOpening}
                onChange={() => toggleNotifPref('emailNewShowdownOpening')}
              />
              <span className="account-toggle__track"><span className="account-toggle__thumb" /></span>
            </span>
          </label>

          <label className="account-toggle-row">
            <div>
              <p className="account-toggle-row__label">Email me if a runner I own is scratched or reinstated</p>
              <p className="account-toggle-row__hint">Includes your refund options and confirmation once you choose.</p>
            </div>
            <span className={`account-toggle ${notifPrefs.emailScratchingAlerts ? 'account-toggle--on' : ''}`}>
              <input
                type="checkbox"
                checked={notifPrefs.emailScratchingAlerts}
                onChange={() => toggleNotifPref('emailScratchingAlerts')}
              />
              <span className="account-toggle__track"><span className="account-toggle__thumb" /></span>
            </span>
          </label>

          <label className="account-toggle-row">
            <div>
              <p className="account-toggle-row__label">Email me stable share/invite activity</p>
              <p className="account-toggle-row__hint">Off by default — updates when someone interacts with a stable you've shared.</p>
            </div>
            <span className={`account-toggle ${notifPrefs.emailStableShare ? 'account-toggle--on' : ''}`}>
              <input
                type="checkbox"
                checked={notifPrefs.emailStableShare}
                onChange={() => toggleNotifPref('emailStableShare')}
              />
              <span className="account-toggle__track"><span className="account-toggle__thumb" /></span>
            </span>
          </label>

          <label className="account-toggle-row">
            <div>
              <p className="account-toggle-row__label">Marketing &amp; news emails</p>
              <p className="account-toggle-row__hint">Optional and off by default. Product news, promotions and offers.</p>
            </div>
            <span className={`account-toggle ${notifPrefs.emailMarketing ? 'account-toggle--on' : ''}`}>
              <input
                type="checkbox"
                checked={notifPrefs.emailMarketing}
                onChange={() => toggleNotifPref('emailMarketing')}
              />
              <span className="account-toggle__track"><span className="account-toggle__thumb" /></span>
            </span>
          </label>
        </div>

        <p className="account-section__desc account-section__desc--footnote">
          Gameplay notifications help you follow your stable. You can change these settings anytime.
        </p>

        {notifStatus && (
          <p className={`account-status account-status--${notifStatus.type}`}>
            {notifStatus.msg}
          </p>
        )}
        <button
          className="account-btn account-btn--primary"
          onClick={saveNotifPrefs}
          disabled={savingNotifPrefs}
          style={{ opacity: savingNotifPrefs ? 0.6 : 1 }}
        >
          {savingNotifPrefs ? 'Saving…' : 'Save Preferences'}
        </button>
      </div>

      {/* ── Security ─────────────────────────────────────────── */}
      <div className="account-section">
        <h2 className="account-section__heading">Security</h2>
        <div className="account-form">
          <div className="account-form__field">
            <label className="account-form__label">Current Password</label>
            <input
              className="account-form__input"
              type="password"
              value={currentPassword}
              onChange={e => setCurrentPassword(e.target.value)}
              placeholder="Enter current password"
            />
          </div>
          <div className="account-form__field">
            <label className="account-form__label">New Password</label>
            <input
              className="account-form__input"
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder="Min 8 characters"
            />
          </div>
          <div className="account-form__field">
            <label className="account-form__label">Confirm New Password</label>
            <input
              className="account-form__input"
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              placeholder="Repeat new password"
            />
          </div>
        </div>
        {passwordStatus && (
          <p className={`account-status account-status--${passwordStatus.type}`}>
            {passwordStatus.msg}
          </p>
        )}
        <button className="account-btn account-btn--primary" onClick={changePassword}>
          Update Password
        </button>

        {/* PIN management link */}
        <div className="account-section__divider" />
        <div className="account-info-row">
          <div>
            <p className="account-info-row__label">Account PIN</p>
            <p className="account-info-row__value">
              {userDoc?.pinEnabled ? '✓ PIN enabled' : 'No PIN set'}
            </p>
          </div>
          <button className="account-btn account-btn--secondary" onClick={() => navigate('/pin-setup')}>
            {userDoc?.pinEnabled ? 'Change PIN' : 'Set PIN'}
          </button>
        </div>
      </div>

      {/* ── Account info ─────────────────────────────────────── */}
      <div className="account-section">
        <h2 className="account-section__heading">Account Info</h2>
        <div className="account-info-row">
          <div>
            <p className="account-info-row__label">Email Address</p>
            <p className="account-info-row__value">{user?.email}</p>
          </div>
          <span className="account-info-row__verified">✓ Verified</span>
        </div>
        <div className="account-info-row">
          <div>
            <p className="account-info-row__label">Member Since</p>
            <p className="account-info-row__value">
              {userDoc?.createdAt?.toDate
                ? userDoc.createdAt.toDate().toLocaleDateString('en-AU', {
                    day: 'numeric', month: 'long', year: 'numeric'
                  })
                : '—'}
            </p>
          </div>
        </div>
        <div className="account-info-row">
          <div>
            <p className="account-info-row__label">Age Verification</p>
            <p className="account-info-row__value">
              {userDoc?.ageVerified ? '✓ Verified (18+)' : 'Not verified'}
            </p>
          </div>
        </div>
      </div>

      {/* ── Danger zone ──────────────────────────────────────── */}
      <div className="account-section account-section--danger">
        <h2 className="account-section__heading account-section__heading--danger">
          Danger Zone
        </h2>
        <div className="account-info-row">
          <div>
            <p className="account-info-row__label">Self Exclusion</p>
            <p className="account-info-row__value account-info-row__value--muted">
              Temporarily pause your account
            </p>
          </div>
          <button
            className="account-btn account-btn--warning"
            onClick={() => navigate('/self-exclude')}
          >
            Self Exclude
          </button>
        </div>
        <div className="account-info-row">
          <div>
            <p className="account-info-row__label">Delete Account</p>
            <p className="account-info-row__value account-info-row__value--muted">
              Permanently remove your account and all data
            </p>
          </div>
          <button
            className="account-btn account-btn--danger"
            onClick={() => {
              if (window.confirm(
                'This will permanently delete your account and all your entries. This cannot be undone. Are you sure?'
              )) navigate('/delete-account');
            }}
          >
            Delete Account
          </button>
        </div>
      </div>
    </div>
  );
}

export { AccountPage };
export default AccountPage;
